import dayjsLib from 'dayjs';
import customParseFormatPlugin from 'dayjs/plugin/customParseFormat';
import durationPlugin from 'dayjs/plugin/duration';
import relativeTimePlugin from 'dayjs/plugin/relativeTime';
import utcPlugin from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import localeData from 'dayjs/plugin/localeData';
import Lang from '../lang/en';
import { SelectOption } from '@app/common/types';

dayjsLib.extend(customParseFormatPlugin);
dayjsLib.extend(durationPlugin);
dayjsLib.extend(relativeTimePlugin);
dayjsLib.extend(utcPlugin);
dayjsLib.extend(timezonePlugin);
dayjsLib.extend(localeData);

export const dayjs = dayjsLib;

export const Validator = {
  passwordFormatRegex: /(?=^.{8,}$)(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?!.*\s)[0-9a-zA-Z!*^?]/,
  emailRegex: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
};

export const timeFormat = 'HH:mm';
export const stateDateFormat = 'YYYY-MM-DD';
export const dateFormat = 'DD/MM/YYYY';
export const dateFriendlyFormat = 'D MMM YYYY';
export const dateTimeFormat = 'HH:mm DD/MM/YYYY';
export const dateTime12FriendlyFormat = 'D MMM YYYY h:mmA';
export const dateTime24FriendlyFormat = 'D MMM YYYY HH:mm';
export const dateTime24FriendlyFormatWithComma = 'D MMM YYYY, HH:mm';
export const timestampFormat = 'YYYY-MM-DD HH:mm:ssZZ';
export const parseApiFormat = 'YYYY-MM-DD HH:mm:ss';
export const timestampApiFormat = 'timestamp';
export const datepickerDateFormats = ['d MMM yyyy', 'd/M/yyyy'];

let defaultTimeZone = 'utc';

export const setDefaultTimeZone = (timezone: string): void => {
  if (!timezone) {
    return;
  }
  defaultTimeZone = timezone;
  dayjsLib.tz.setDefault(timezone);
};

export const matchInUrl: (url: string) => boolean = (url) => {
  const re = new RegExp('\\b' + url + '\\b');
  return re.test(window.location.href);
};

export const applyEllipsis = (text: string, quantity: number): string => {
  if (text.length > quantity - 1) {
    text = text.substring(0, quantity - 1) + '...';
  }
  return text;
};

export const secondsDifference = (
  fromDate: string,
  toDate?: string
): null | number => {
  if (!fromDate) {
    return null;
  }

  const refineDateString =
    typeof fromDate === 'string'
      ? fromDate.replace('+00', '.000000Z')
      : fromDate;
  const startDate = new Date(refineDateString);
  const endDate = toDate ? new Date(toDate) : new Date();
  const diffInSeconds = (endDate.getTime() - startDate.getTime()) / 1000;
  return Math.round(diffInSeconds);
};

export interface SelectOptionI {
  value: string;
  label: string;
}

const leftPadHours = (value: number) => (value < 10 ? `0${value}` : `${value}`);

export const hourOptions: SelectOptionI[] = Array.from(Array(24).keys()).map(
  (i) => {
    const minutes = i % 1 === 0 ? '00' : '30';
    const hour = Math.floor(i);
    const formattedHour = leftPadHours(hour);
    const time = `${formattedHour}:${minutes}`;

    return {
      value: time,
      label: time,
    };
  }
);

export const fullHourOptions: SelectOptionI[] = Array.from(
  Array(24).keys()
).map((i) => {
  const formattedHour = leftPadHours(i);

  return {
    value: formattedHour,
    label: formattedHour,
  };
});

export const minutesOptions: SelectOptionI[] = Array.from(Array(6).keys()).map(
  (i) => {
    const minutes = `${i}0`;

    return {
      value: minutes,
      label: minutes,
    };
  }
);

export const allowNumbersOnly = (event: {
  which: number;
  keyCode: number;
  preventDefault: () => void;
}): void => {
  const key = event.which ? event.which : event.keyCode;
  if (
    !(
      key === 8 ||
      key === 9 ||
      key === 13 ||
      key === 46 ||
      key === 110 ||
      (key >= 35 && key <= 40) ||
      (key >= 48 && key <= 57) ||
      (key >= 96 && key <= 105)
    )
  ) {
    event.preventDefault();
  }
};

export const formatUTCStringAsApiTimestamp = (
  value?: Date | string | number | null,
  displayFormat = dateTimeFormat,
  timezone = defaultTimeZone
): string =>
  value ? dayjs(value).utc(true).tz(timezone).format(displayFormat) : '';

export const buildApiTimestamp = (
  value?: Date | number | dayjsLib.Dayjs
): string => dayjs(value).utc().format(parseApiFormat);

export const formatStateDate = (value: Date | null): string | null =>
  value ? dayjs(value).format(stateDateFormat) : value;

// deprecated, please use formatUTCStringAsApiTimestamp() and ensure, that BE returns UTC time
export const deprecatedDateFormatting = (
  date: string | null,
  friendly = false
): string => {
  if (!date) {
    return '';
  }

  return dayjs(date).format(friendly ? dateFriendlyFormat : dateFormat);
};

export const getHumanizeDate = (
  date: string,
  returnDateFormat?: string
): string => {
  if (!date) {
    return '';
  }
  const today = dayjs();
  const dayDuration = 24 * 60 * 60 * 1000;
  const timeDiff = today.diff(date);
  const humanized =
    timeDiff < dayDuration
      ? Lang.humanizedDateToday
      : `${dayjs.duration(timeDiff).humanize()} ${Lang.humanizedDateAgo}`;

  return `${
    returnDateFormat ? `${dayjs(date).format(returnDateFormat)}, ` : ''
  } ${humanized}`;
};

type LogLevel = 'critical' | 'error' | 'warning' | 'debug' | 'info';

const getConsoleLogger = (level: LogLevel): ((...args: string[]) => void) => {
  switch (level) {
    case 'critical':
    case 'error':
      return console.error;
    case 'warning':
      return console.warn;
    case 'info':
      return console.info;
    default:
      return console.log;
  }
};

export const log = (level: LogLevel, ...messages: string[]): void => {
  const consoleLogger = getConsoleLogger(level);

  consoleLogger(...messages);

  if (window.Rollbar) {
    window.Rollbar[level](messages.join('\n'));
  }
};

export const getLast7Days = (): string[] => {
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const today = new Date().getDay();
  return [...days.splice(today), ...days.splice(0, today)];
};

export const getMonthsOptions = (): SelectOption[] => {
  return dayjs.months().map((item, index) => ({
    value: index + 1 < 10 ? `0${index + 1}` : `${index + 1}`,
    label: item,
  }));
};

export const getDayOfWeekOptions = (): SelectOption[] => {
  return dayjs.weekdays().map((item, index) => ({
    value: `${index}`,
    label: item,
  }));
};
